import type { FC, KeyboardEvent, MouseEvent, ReactNode } from "react";
import { cn } from "../../utils";
import { Badge } from "../badge";
import { Icon } from "../icon";

import { ConversationLinkDate } from "./conversation-link-date";

export interface AttachmentPreview {
  fileUploadId: string;
  filename: string;
  mimeType: string;
}

export interface ConversationLinkContentProps {
  icon?: ReactNode;
  from?: ReactNode;
  date?: string;
  subject?: string;
  badges?: ReactNode;
  isUnread?: boolean;
  isStarred?: boolean;
  isCurrent?: boolean;
  insuredId?: string | null;
  attachmentCount?: number;
  attachmentPreviews?: AttachmentPreview[];
  onAttachmentPreviewOpen?: (attachment: AttachmentPreview) => void;
  displayLabels?: string[];
  compactLabels?: boolean;
  draftCount?: number;
  messageCount?: number;
}

export const getAttachmentPreviewMimeType = ({ mimeType }: Pick<AttachmentPreview, "mimeType">) =>
  mimeType.split(";")[0]?.trim().toLowerCase() || mimeType;

const getAttachmentTypeLabel = ({ filename, mimeType }: AttachmentPreview) => {
  const extension = filename.split(".").pop();

  if (extension && extension !== filename && extension.length <= 8) {
    return extension.toUpperCase();
  }

  const baseMimeType = getAttachmentPreviewMimeType({ mimeType });
  const [, subtype] = baseMimeType?.split("/") ?? [];
  const label = subtype || baseMimeType || "file";

  return label.split(/[.+-]/)[0]?.toUpperCase() || "FILE";
};

export const ConversationLinkContent: FC<ConversationLinkContentProps> = ({
  attachmentCount = 0,
  attachmentPreviews = [],
  compactLabels,
  displayLabels = [],
  draftCount = 0,
  icon,
  from,
  date,
  subject,
  badges,
  isUnread,
  isStarred,
  isCurrent,
  insuredId,
  messageCount = 0,
  onAttachmentPreviewOpen,
}) => {
  const hiddenAttachmentCount = Math.max(attachmentCount - attachmentPreviews.length, 0);
  const hasLabels = displayLabels.length > 0;
  const unreadTextWeight = isUnread && "font-semibold";
  const selectedAttachmentClassName =
    isCurrent && "border-primary-foreground/15 bg-background/10 text-primary-foreground/65";
  const labelCountText = `${displayLabels.length} ${displayLabels.length === 1 ? "label" : "labels"}`;
  const labelSummaryText = hasLabels ? `${labelCountText}: ${displayLabels.join(", ")}` : undefined;
  const hasSelectorIcon = Boolean(icon);
  // A count of 1 says nothing, so only a real thread earns the space next to the subject.
  const threadCountText = messageCount > 1 ? `${messageCount} messages` : undefined;

  return (
    <>
      <div
        className={cn(
          "grid items-center justify-items-center self-start py-0.75",
          hasSelectorIcon ? "@max-3xl:grid-cols-1 @max-3xl:gap-2.5 @3xl:grid-cols-[3rem_1rem]" : "grid-cols-[1rem]"
        )}
      >
        {hasSelectorIcon && <div className="flex w-(--gutter) items-center justify-center">{icon}</div>}
        <div className="flex size-4 items-center justify-center">
          {isStarred ? (
            <Icon
              icon="star"
              aria-hidden="true"
              className={cn("filled text-sm text-yellow-500", isCurrent && "text-inherit")}
            />
          ) : isUnread ? (
            <Icon
              icon="circle"
              aria-hidden="true"
              className={cn("filled text-2xs text-primary", isCurrent && "text-inherit")}
            />
          ) : null}
        </div>
      </div>

      <div className={cn("min-w-0 gap-3 @max-3xl:space-y-1.5 @3xl:flex @3xl:items-start", unreadTextWeight)}>
        <div className="flex flex-none gap-1.5 items-center min-w-0 text-base @3xl:w-48">
          <span className="flex-auto truncate text-sm">{from ?? "Unknown Sender"}</span>
          {draftCount > 0 && (
            <span className="flex-none text-destructive">{draftCount > 1 ? `Draft ${draftCount}` : "Draft"}</span>
          )}
          {attachmentCount > 0 && attachmentPreviews.length === 0 && <Icon icon="attachment" className="opacity-50" />}
        </div>
        <div className="min-w-0 flex-auto space-y-1">
          <div className="flex min-w-0 items-center gap-2">
            {/* Subject and count share the growing slot so the chip stays pinned to the end of the
                subject text instead of drifting to the far right of a short subject's row. */}
            <div className="flex min-w-0 flex-auto items-center gap-1.5">
              <span className="min-w-0 text-sm truncate">{subject || "(No Subject)"}</span>
              {threadCountText && (
                <Badge
                  variant="secondary"
                  type="square"
                  size="2xs"
                  title={threadCountText}
                  aria-label={threadCountText}
                  className={cn(
                    "flex-none font-normal text-foreground/60",
                    isCurrent && "bg-background/15 text-primary-foreground/65"
                  )}
                >
                  {messageCount}
                </Badge>
              )}
            </div>
            {hasLabels && compactLabels && (
              <Badge
                variant="secondary"
                type="square"
                size="2xs"
                title={labelSummaryText}
                aria-label={labelSummaryText}
                className={cn("flex-none gap-1", isCurrent && "bg-background/15 text-primary-foreground")}
              >
                <Icon icon="label" className="text-xs opacity-80" />
                <span>{displayLabels.length}</span>
              </Badge>
            )}
            {hasLabels && !compactLabels && (
              <div className="flex flex-none flex-wrap gap-1.5 items-center overflow-hidden">
                {displayLabels.map((label, index) => (
                  <Badge
                    key={`${label}-${index}`}
                    variant="secondary"
                    type="square"
                    className={cn("max-w-32", isCurrent && "bg-background/15 text-primary-foreground")}
                  >
                    <span className="truncate">{label}</span>
                  </Badge>
                ))}
              </div>
            )}
            {badges}
          </div>
          {attachmentPreviews.length > 0 && (
            <div className="flex min-w-0 flex-wrap gap-1 overflow-hidden">
              {attachmentPreviews.map((attachment, index) => (
                <ConversationAttachmentPreviewBadge
                  key={`${attachment.filename}-${attachment.mimeType}-${index}`}
                  attachment={attachment}
                  isCurrent={isCurrent}
                  onAttachmentPreviewOpen={onAttachmentPreviewOpen}
                />
              ))}
              {hiddenAttachmentCount > 0 && (
                <Badge
                  variant="outline"
                  type="square"
                  size="2xs"
                  className={cn(
                    "flex-none border-foreground/10 font-normal text-foreground/55",
                    selectedAttachmentClassName
                  )}
                >
                  +{hiddenAttachmentCount}
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>

      <div
        className={cn(
          "@3xl:flex @3xl:flex-row-reverse gap-3 items-center self-start @3xl:pt-0.5 @max-3xl:space-y-1.5 text-right text-sm",
          unreadTextWeight
        )}
      >
        <div className="flex-none text-right text-xs opacity-50">{date && <ConversationLinkDate date={date} />}</div>
        {insuredId && <Icon icon="link" className="opacity-50 text-sm" />}
      </div>
    </>
  );
};

const ConversationAttachmentPreviewBadge: FC<{
  attachment: AttachmentPreview;
  isCurrent?: boolean;
  onAttachmentPreviewOpen?: (attachment: AttachmentPreview) => void;
}> = ({ attachment, isCurrent, onAttachmentPreviewOpen }) => {
  const canOpen = Boolean(onAttachmentPreviewOpen);

  const handleClick = (event: MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onAttachmentPreviewOpen?.(attachment);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    onAttachmentPreviewOpen?.(attachment);
  };

  const handleKeyUp = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.stopPropagation();
    }
  };

  return (
    <Badge
      variant="outline"
      type="square"
      size="2xs"
      title={attachment.filename}
      role={canOpen ? "button" : undefined}
      tabIndex={canOpen ? 0 : undefined}
      aria-label={canOpen ? `Open ${attachment.filename}` : undefined}
      onClick={canOpen ? handleClick : undefined}
      onKeyDown={canOpen ? handleKeyDown : undefined}
      onKeyUp={canOpen ? handleKeyUp : undefined}
      className={cn(
        "max-w-44 gap-1.5 border-foreground/10 font-normal text-foreground/55",
        canOpen &&
          "cursor-pointer hover:border-foreground/20 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        isCurrent && "border-primary-foreground/15 bg-background/10 text-primary-foreground/65"
      )}
    >
      <span className="truncate">{attachment.filename}</span>
      <span className="flex-none text-[10px] uppercase opacity-60">{getAttachmentTypeLabel(attachment)}</span>
    </Badge>
  );
};
